import { Spinner } from '@components/ui/Primitives'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa'
import styled, { useTheme } from 'styled-components'
import { ThumbnailWrapper, VIEW_HEIGHT, VIEW_WIDTH } from './WorkflowThumbnail'

interface Props {
  onCreate: () => void
  isCreating: boolean
}

const GHOST_NODES = [
  { x: 30, y: 30 },
  { x: 106, y: 30 }
]

const NODE_WIDTH = 54
const NODE_HEIGHT = 36
const PLUS_CENTER_X = 192
const PLUS_CENTER_Y = 48
const PLUS_RADIUS = 18

const GhostWorkflowThumbnail: React.FC = () => {
  const theme = useTheme()

  const stroke = theme.colors.iconGray
  const dash = '4 5'

  const connectorTargets = [GHOST_NODES[1].x, PLUS_CENTER_X - PLUS_RADIUS]

  return (
    <svg width="100%" height={VIEW_HEIGHT} viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}>
      {GHOST_NODES.map((node, index) => (
        <path
          key={node.x}
          d={`M ${node.x + NODE_WIDTH} ${PLUS_CENTER_Y} H ${connectorTargets[index]}`}
          fill="none"
          stroke={stroke}
          strokeWidth={1.2}
          strokeDasharray={dash}
          opacity={0.45}
        />
      ))}

      {GHOST_NODES.map(node => (
        <g key={node.x}>
          <rect
            x={node.x}
            y={node.y}
            width={NODE_WIDTH}
            height={NODE_HEIGHT}
            rx={4}
            fill={theme.colors.neutralWhite}
            fillOpacity={0.75}
            stroke={stroke}
            strokeWidth={1.2}
            strokeDasharray={dash}
            opacity={0.7}
          />
          <rect
            x={node.x}
            y={node.y}
            width={NODE_WIDTH}
            height={5}
            rx={2}
            fill={stroke}
            opacity={0.3}
          />
        </g>
      ))}

      <circle
        className="ghost-plus"
        cx={PLUS_CENTER_X}
        cy={PLUS_CENTER_Y}
        r={PLUS_RADIUS}
        fill={theme.colors.neutralWhite}
        stroke={theme.colors.success}
        strokeWidth={1.4}
      />
      <path
        d={`M ${PLUS_CENTER_X} ${PLUS_CENTER_Y - 7} v 14 M ${
          PLUS_CENTER_X - 7
        } ${PLUS_CENTER_Y} h 14`}
        stroke={theme.colors.success}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  )
}

export const WorkflowCreateCard: React.FC<Props> = ({ onCreate, isCreating }) => {
  const [t] = useTranslation()

  return (
    <StyledCard type="button" disabled={isCreating} onClick={onCreate}>
      <ThumbnailWrapper>
        <GhostWorkflowThumbnail />
      </ThumbnailWrapper>

      <CardBody>
        <TitleRow>
          {isCreating ? <Spinner size={16} /> : <FaPlus />}
          <Title>{t('workflows.createTile.title')}</Title>
        </TitleRow>
        <Description>{t('workflows.createTile.description')}</Description>
      </CardBody>
    </StyledCard>
  )
}

const StyledCard = styled.button`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  padding: 0;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  .ghost-plus {
    transition: fill 0.15s ease;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    box-shadow: ${({ theme }) => theme.shadow.md};
    transform: translateY(-2px);

    .ghost-plus {
      fill: color-mix(in srgb, ${({ theme }) => theme.colors.success} 12%, white);
    }
  }

  &:disabled {
    cursor: default;
  }
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme }) => theme.colors.success};

  > svg {
    width: 0.875rem;
    height: 0.875rem;
    flex-shrink: 0;
  }
`

const Title = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const Description = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.iconGray};
`
